import { isCustomUrl } from 'components/scenarios/create/status-checks/third-party-presets'
import {
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  getExternalIntegrationForObservabilityTool,
  OBSERVABILITY_TOOLS
} from 'components/status-checks/authentication-form-utils'
import { isExternalIntegrationOfType } from 'components/status-checks/authentication-form-validation-utils'
import { DynatraceEntitySelectionSource } from 'components/status-checks/form/status-check/dynatrace/DynatraceMonitorSelection'
import { GrafanaSelectionSource } from 'components/status-checks/form/status-check/grafana/GrafanaMonitorSelection'
import { PrometheusSelectionSource } from 'components/status-checks/form/status-check/prometheus/prometheus-monitor-utils'
import {
  getDynatraceSelectorFromRequestParams,
  isDynatraceSelectorEntityIdType,
  isDynatraceSelectorK8sQueryType
} from 'components/status-checks/status-checks-dynatrace-form-utils'
import { getExistingMonitorUrlPredicateFn } from 'components/status-checks/status-checks-form-validation-utils'
import {
  type EndpointConfiguration,
  type EvaluationConfigurationCreateRequest,
  ObservabilityToolType,
  type ResponseBodyEvaluation,
  type ServiceStatusCheckFromStatusCheck,
  type StatusCheck,
  StatusCheckCategory,
  type StatusCheckEvaluationPredicate,
  type StatusCheckTeamExternalIntegration,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { type MultiValueTags } from 'types/tags'
import { getAWSAccountIdFromIamArn } from 'utils/status-checks/status-check-integrations-util-aws'
import {
  datadogMonitorRegex,
  getDatadogMonitorIdFromUrl
} from 'utils/status-checks/status-check-integrations-util-datadog'
import { getDatadogEUMonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import { getDatadogUS1FEDMonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import { getDatadogUS3MonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import { getDatadogUS5MonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import { formatResponseBodyEvaluationPredicatesGrafana } from 'utils/status-checks/status-check-integrations-util-grafana'
import { INTEGRATION_NAME } from 'utils/status-checks/status-checks-utils'
import { capitalize } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  computeResponseBodyEvaluation,
  defaultStatusCheckEvaluationConfiguration,
  formatResponseBodyEvaluationPredicates,
  getDomainNameFromIntegration,
  getEndpointConfigurationUrl
} from 'utils/team-external-integrations/team-external-integrations-util'
import { DATADOG_MONITOR_PAGE_URLS } from 'utils/third-party'
import { ms } from 'utils/time/millisecond'

import type {
  AuthenticationFormValues,
  ObservabilityTool
} from 'components/status-checks/authentication-form-utils'
import type {
  DynatraceEntityId,
  DynatraceK8sQuery
} from 'queries/status-checks/pre-flight/types'

export const MIN_OK_LATENCY_MS = 50
export const MAX_OK_LATENCY_MS = ms.seconds(10)

export interface StatusCheckFormEvaluationConfiguration {
  healthyStatusCode: string
  isCustomRequestTimeout: boolean
  requestTimeout: number | null
  responseBodyEvaluation?: ResponseBodyEvaluation
}

export type DynatraceEntitySelectionAsProblemsUrl = {
  type: DynatraceEntitySelectionSource.PROBLEMS_URL
  entityId?: never
  k8sQuery?: never
}

export type DynatraceEntitySelectionAsEntityId = {
  type: DynatraceEntitySelectionSource.ENTITY_ID
  entityId: DynatraceEntityId
  k8sQuery?: never
}

export type DynatraceEntitySelectionAsK8sTargeting = {
  type: DynatraceEntitySelectionSource.K8S_TARGETING
  entityId?: never
  k8sQuery?: DynatraceK8sQuery
}

export type DynatraceEntitySelector =
  | DynatraceEntitySelectionAsProblemsUrl
  | DynatraceEntitySelectionAsEntityId
  | DynatraceEntitySelectionAsK8sTargeting

export type GrafanaSelectionAsSearch = {
  type: GrafanaSelectionSource.SEARCH
}

export type GrafanaSelectionAsAlertUrl = {
  type: GrafanaSelectionSource.ALERT_URL
}

export type GrafanaSelector =
  | GrafanaSelectionAsSearch
  | GrafanaSelectionAsAlertUrl

export type PrometheusSelector = {
  type: PrometheusSelectionSource
}

/**
 * Types, Enums, and Interfaces
 */
export interface StatusCheckFormValues extends AuthenticationFormValues {
  category: StatusCheckCategory
  dynatraceSelector?: DynatraceEntitySelector
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  grafanaSelector?: GrafanaSelector
  healthyStatusCode: string
  method?: 'GET' | 'POST'
  monitorUrl: string
  multiSelectTags?: MultiValueTags | undefined
  pollingIntervalSeconds: string
  prometheusSelector?: PrometheusSelector
  requestTimeout?: number | null
  statusCheckName: string
}

export enum StatusCheckMonitorSelectionRadioValue {
  URL = 'url',
  API = 'api',
  SEARCH = 'search'
}

export enum StatusCheckCategoryLabel {
  ERRORS = 'Errors',
  LATENCY = 'Latency',
  REQUESTS = 'Requests',
  UNKNOWN = 'Unknown'
}

// equivalent of 50Kb
export const STATUS_CHECK_PAYLOAD_MAX_CONTENT_LENGTH = 51_200
export const STATUS_CHECK_CATEGORIES = [
  [StatusCheckCategory.ERRORS, StatusCheckCategoryLabel.ERRORS],
  [StatusCheckCategory.LATENCY, StatusCheckCategoryLabel.LATENCY],
  [StatusCheckCategory.REQUESTS, StatusCheckCategoryLabel.REQUESTS],
  [StatusCheckCategory.UNKNOWN, StatusCheckCategoryLabel.UNKNOWN]
] as const

export const STATUS_CHECK_CATEGORIES_LOOKUP = Object.fromEntries(
  STATUS_CHECK_CATEGORIES
)

/*
 * This value establishes a consistent width for images inserted into the menu items of MUI's Select component. It is
 * necessary to hard define this in order to prevent the Select component's textfield from expanding and causing UI/UX
 * elements to become mis-aligned
 */
export const LIST_ITEM_MIN_WIDTH = 36
export const DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME = ms.seconds(10)

/**
 * Converts any valid Datadog US1 monitor URL to a standard format for validation purposes only.
 */
export function convertDatadogAppUrlToApiUrl(url = '') {
  if (datadogMonitorRegex.test(url)) {
    const isAPPSubDomain = datadogMonitorRegex.exec(url)?.[1] === 'app'
    const isAPIUrl = datadogMonitorRegex.exec(url)?.[2] === 'api/v1/monitor'
    const monitorId = datadogMonitorRegex.exec(url)?.[3]

    if (isAPIUrl) {
      return url
    }

    // Convert any "https://app..." URLs to "https://api..."
    if (isAPPSubDomain || monitorId) {
      return `https://api.datadoghq.com/api/v1/monitor/${monitorId}`
    }
  }
}

/**
 * Matches an existing status check to the currently selected observability tool and monitor URL.
 */
export function getExistingStatusCheckByMonitorUrl(
  existingStatusChecks: ServiceStatusCheckFromStatusCheck[],
  observabilityTool: ObservabilityTool,
  monitorUrl: string,
  requestMethod?: EndpointConfiguration['method']
) {
  const isDatadogObservabilityTool =
    observabilityTool.type === ObservabilityToolType.DATADOG

  const existingStatusPredicateFn = getExistingMonitorUrlPredicateFn(
    observabilityTool.type
  )

  return (
    existingStatusChecks
      // Filter existingStatusChecks to match currently selected observability tool type and name
      .filter((statusCheck) =>
        existingStatusPredicateFn(statusCheck, observabilityTool, requestMethod)
      )
      .find((filteredExistingStatusCheck) => {
        if (isDatadogObservabilityTool) {
          /*
           * In order for this check to work with Datadog integrations, we have to convert both the
           * existing status check URL and the monitor URL to Datadog's API URL format
           */
          const existingDatadogStatusCheckApiUrl = convertDatadogAppUrlToApiUrl(
            filteredExistingStatusCheck.integration.url
          )
          const datadogMonitorApiUrl = convertDatadogAppUrlToApiUrl(monitorUrl)

          return existingDatadogStatusCheckApiUrl === datadogMonitorApiUrl
        }

        return filteredExistingStatusCheck.integration.url === monitorUrl
      })
  )
}

/**
 * Builds a TeamExternalIntegration object for a Status Check from the current form values
 */
export function buildTeamExternalIntegration({
  externalIntegration,
  headers,
  isPrivateNetworkIntegration,
  observabilityTool,
  observabilityToolNickname,
  apiBaseUrl,
  authenticationUrl,
  monitorUrl,
  domain,
  awsAccessKeyID,
  awsSecretAccessKey,
  awsIamRoleArn,
  awsAuthenticationType
}: StatusCheckFormValues | AuthenticationFormValues): TeamExternalIntegration {
  const newExternalIntegration = {
    ...externalIntegration,
    headers,
    isPrivateNetworkIntegration,
    name: externalIntegration?.name ?? '',
    observabilityToolType: observabilityTool.type,
    type:
      externalIntegration?.type ??
      observabilityTool.integrationType ??
      IntegrationType.Custom
  }

  const isAWSIntegration = isExternalIntegrationOfType(
    IntegrationType.AWS,
    newExternalIntegration
  )
  const isCustomIntegration = isExternalIntegrationOfType(
    IntegrationType.Custom,
    newExternalIntegration
  )
  const isDatadogIntegration = isExternalIntegrationOfType(
    IntegrationType.Datadog,
    newExternalIntegration
  )
  const isPrometheusIntegration = isExternalIntegrationOfType(
    IntegrationType.Prometheus,
    newExternalIntegration
  )

  newExternalIntegration.domain = isDatadogIntegration
    ? null
    : domain?.name || null

  newExternalIntegration.url =
    monitorUrl || authenticationUrl || apiBaseUrl || ''

  // monitorUrl should override all others
  if (isCustomIntegration) {
    newExternalIntegration.name = observabilityToolNickname ?? ''
  }

  if (isAWSIntegration) {
    if (
      awsIamRoleArn &&
      awsAuthenticationType === AWSAuthenticationType.IAMRole
    ) {
      const domainName = getAWSAccountIdFromIamArn(awsIamRoleArn)

      newExternalIntegration.domain = domainName
      newExternalIntegration.integrationSpecificValues = {
        AWS_IAM_ROLE_ARN: awsIamRoleArn
      }
    } else {
      newExternalIntegration.domain = domain?.name || null
      newExternalIntegration.integrationSpecificValues = {
        AWS_ACCESS_KEY_ID: awsAccessKeyID,
        AWS_SECRET_ACCESS_KEY: awsSecretAccessKey
      }
    }
    newExternalIntegration.name = ObservabilityToolType.AWS
  }

  if (isPrometheusIntegration) {
    newExternalIntegration.name = capitalize(ObservabilityToolType.PROMETHEUS)
  }

  if (observabilityTool.integrationType === IntegrationType.Grafana) {
    newExternalIntegration.name =
      INTEGRATION_NAME[ObservabilityToolType.GRAFANA]
  }

  return newExternalIntegration
}

function validatePredicate(predicate: StatusCheckEvaluationPredicate) {
  return (
    predicate.type !== null &&
    predicate.comparator !== '' &&
    predicate.jpQuery !== '' &&
    predicate.rValue !== ''
  )
}

/**
 * Builds a StatusCheckEvaluationConfiguration object from passed in form values.
 */
export function buildEvaluationConfigurationFromFormValues(
  formValues: StatusCheckFormValues
): StatusCheckFormEvaluationConfiguration {
  const { evaluationConfiguration, healthyStatusCode } = formValues

  const { responseBodyEvaluation, ...evaluationConfigurationRest } =
    evaluationConfiguration || {}

  const predicates = evaluationConfiguration?.responseBodyEvaluation?.predicates

  const arePredicatesPopulated = predicates?.reduce(
    (isValid, predicate) => (validatePredicate(predicate) ? isValid : false),
    true
  )

  return {
    ...evaluationConfigurationRest,
    healthyStatusCode,
    responseBodyEvaluation: {
      op: responseBodyEvaluation?.op ?? 'AND',
      ...(arePredicatesPopulated && { predicates })
    }
  }
}

/**
 * Builds the teamExternalIntegration object for the API call to create a new Status Check
 */
export function buildStatusCheckTeamExternalIntegration(
  formValues: StatusCheckFormValues
): StatusCheckTeamExternalIntegration {
  const integration = buildTeamExternalIntegration(formValues)
  const isCustomIntegration = integration.type === IntegrationType.Custom
  const domainName = formValues.domain?.name

  const statusCheckTeamExternalIntegration: StatusCheckTeamExternalIntegration =
    {
      name: formValues.externalIntegration?.name ?? '',
      observabilityToolType: formValues.observabilityTool.type,
      domain: domainName === 'US1' ? null : domainName // Special case for Datadog's US1 domain
    }

  /**
   * custom integration needs to be configured on the team external integration level first before
   * we can add this object to the request body
   */
  if (
    (isCustomIntegration && integration.isConfigured) ||
    (!isCustomIntegration && !integration.isConfigured)
  ) {
    statusCheckTeamExternalIntegration.name = integration.name
  }

  return statusCheckTeamExternalIntegration
}

export async function buildStatusCheckFormEvaluationConfiguration({
  currentTeamId,
  formValues,
  integration
}: {
  currentTeamId: string
  formValues: StatusCheckFormValues
  integration: TeamExternalIntegration
}): Promise<EvaluationConfigurationCreateRequest> {
  const newEvaluationConfiguration =
    buildEvaluationConfigurationFromFormValues(formValues)

  const evaluationConfiguration: EvaluationConfigurationCreateRequest = {
    ...(formValues.evaluationConfiguration.isCustomRequestTimeout &&
      newEvaluationConfiguration.requestTimeout !== null && {
        okLatencyMaxMs: newEvaluationConfiguration.requestTimeout
      }),
    okStatusCodes: [newEvaluationConfiguration.healthyStatusCode]
  }

  if (
    newEvaluationConfiguration.responseBodyEvaluation &&
    integration.type === IntegrationType.Custom
  ) {
    evaluationConfiguration.responseBodyEvaluation =
      formatResponseBodyEvaluationPredicates(
        newEvaluationConfiguration.responseBodyEvaluation
      )
  } else if (integration.type === IntegrationType.Grafana) {
    evaluationConfiguration.responseBodyEvaluation =
      formatResponseBodyEvaluationPredicatesGrafana(
        await getEndpointConfigurationUrl(integration, currentTeamId)
      )
  } else {
    evaluationConfiguration.responseBodyEvaluation =
      computeResponseBodyEvaluation(integration)
  }

  return evaluationConfiguration
}

export function getDomainNameFromTeamExternalIntegration(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (
    integration?.observabilityToolType === ObservabilityToolType.DATADOG &&
    !integration?.domain
  ) {
    return '/US1'
  }

  return integration?.domain ? `/${integration.domain}` : ''
}

function getObservabilityToolFromExistingStatusCheck(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  domainName: string
) {
  const {
    domain,
    name: observabilityToolName = '',
    type: integrationType,
    observabilityToolType
  } = statusCheck.integration

  const observabilityTool: ObservabilityTool = {
    name: observabilityToolName
  }

  if (observabilityToolType) {
    observabilityTool.type = observabilityToolType
  }

  if (integrationType) {
    observabilityTool.integrationType = integrationType
  }

  if (observabilityToolType === ObservabilityToolType.AWS) {
    observabilityTool.selectedDomain = domain ?? undefined
  }

  // Configures any new custom domains to the observability tool so they are immediately available
  if (observabilityTool.type === ObservabilityToolType.CUSTOM && domainName) {
    observabilityTool.domains = [
      {
        name: domain ?? '',
        integrationType: IntegrationType.Custom
      }
    ]
  } else {
    observabilityTool.domains = OBSERVABILITY_TOOLS.find(
      (staticObservabilityTool) =>
        staticObservabilityTool.type === observabilityTool.type
    )?.domains
  }

  return observabilityTool
}

/**
 * Returns default form values for the Create Status Check form
 */
export function getDefaultStatusCheckFormValues(): StatusCheckFormValues {
  return {
    apiBaseUrl: '',
    authenticationUrl: '',
    authentication: {
      username: '',
      password: ''
    },
    awsAccessKeyID: '',
    awsSecretAccessKey: '',
    awsAuthenticationType: AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    awsIamRoleArn: '',
    category: StatusCheckCategory.UNKNOWN,
    customDomainName: '',
    dynatraceSelector: {
      type: DynatraceEntitySelectionSource.K8S_TARGETING
    },
    domain: { name: '' },
    endpointConfiguration: { method: 'GET' },
    evaluationConfiguration: defaultStatusCheckEvaluationConfiguration,
    externalIntegration: undefined,
    grafanaSelector: {
      type: GrafanaSelectionSource.SEARCH
    },
    headers: [],
    healthyStatusCode:
      defaultStatusCheckEvaluationConfiguration.healthyStatusCode,
    isPrivateNetworkIntegration: false,
    monitorUrl: '',
    multipleRegions: false,
    observabilityTool: { name: '' },
    observabilityToolNickname: '',
    pollingIntervalSeconds: '',
    prometheusSelector: {
      type: PrometheusSelectionSource.RULES
    },
    statusCheckName: ''
  }
}

/**
 * Build default EditStatusCheck form values for editing a shared status check
 */
export function getDefaultStatusCheckFormValuesFromStatusCheck(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  externalIntegrations: TeamExternalIntegration[]
): StatusCheckFormValues {
  const {
    category,
    endpointConfiguration,
    multiSelectTags,
    name: statusCheckName = '',
    requestParams
  } = statusCheck

  const {
    domain: observabilityToolDomain,
    isPrivateNetworkIntegration,
    name: observabilityToolName = '',
    type: integrationType,
    observabilityToolType,
    url: monitorUrl = ''
  } = statusCheck.integration

  const isCustomObservabilityTool =
    observabilityToolType === ObservabilityToolType.CUSTOM
  const isAWSObservabilityTool =
    observabilityToolType === ObservabilityToolType.AWS
  const isDynatraceObservabilityTool =
    observabilityToolType === ObservabilityToolType.DYNATRACE
  const isGrafanaObservabilityTool =
    observabilityToolType === ObservabilityToolType.GRAFANA
  const isPrometheusObservabilityTool =
    observabilityToolType === ObservabilityToolType.PROMETHEUS

  const domainName = getDomainNameFromIntegration(statusCheck.integration) ?? ''

  const observabilityTool: ObservabilityTool =
    getObservabilityToolFromExistingStatusCheck(statusCheck, domainName)

  const observabilityToolNickname = isCustomObservabilityTool
    ? observabilityToolName
    : ''

  const domain = domainName
    ? { name: domainName, integrationType }
    : { name: '' }

  const customDomainName =
    isCustomObservabilityTool && observabilityTool.domains?.length
      ? observabilityToolDomain ?? undefined
      : ''

  const apiBaseUrl = !isCustomObservabilityTool ? monitorUrl : ''

  const authenticationUrl =
    isCustomObservabilityTool || isAWSObservabilityTool ? monitorUrl : ''

  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  const { headers = [] } = externalIntegration ?? {}

  const evaluationConfiguration =
    statusCheck.evaluationConfiguration ||
    defaultStatusCheckEvaluationConfiguration

  const healthyStatusCode =
    statusCheck?.evaluationConfiguration?.healthyStatusCode ||
    defaultStatusCheckEvaluationConfiguration.healthyStatusCode

  const pollingIntervalSeconds = `${statusCheck.pollingIntervalSeconds ?? ''}`

  const configPredicates =
    statusCheck?.evaluationConfiguration?.responseBodyEvaluation?.predicates

  /*
   * Defaulting to an empty array here will insure that we do not render an empty
   * 'predicates' fieldset when modifying a HC with a GET method configuration.
   */
  const predicates = configPredicates?.length ? configPredicates : []

  const requestTimeout =
    statusCheck?.evaluationConfiguration?.requestTimeout ??
    defaultStatusCheckEvaluationConfiguration.requestTimeout

  const dynatraceSelector = isDynatraceObservabilityTool
    ? getDynatraceSelectorFromRequestParams(requestParams)
    : undefined

  const grafanaSelector = isGrafanaObservabilityTool
    ? ({
        type: GrafanaSelectionSource.ALERT_URL
      } as GrafanaSelectionAsAlertUrl)
    : undefined

  const { method, payload } = statusCheck.endpointConfiguration ?? {}
  const endpointConfigurationApplied = !isCustomObservabilityTool
    ? endpointConfiguration
    : method === 'POST'
    ? {
        method,
        payload,
        headers: {
          ...endpointConfiguration?.headers,
          ['content-type']:
            endpointConfiguration?.headers?.['content-type'] ??
            'application/json'
        }
      }
    : { method }

  return {
    apiBaseUrl,
    authenticationUrl,
    category,
    customDomainName,
    domain,
    endpointConfiguration: endpointConfigurationApplied,
    evaluationConfiguration: {
      ...evaluationConfiguration,
      responseBodyEvaluation: {
        op: evaluationConfiguration.responseBodyEvaluation?.op || 'AND',
        predicates
      },
      isCustomRequestTimeout: Boolean(requestTimeout)
    },
    externalIntegration,
    headers,
    healthyStatusCode,
    isPrivateNetworkIntegration: Boolean(isPrivateNetworkIntegration),
    monitorUrl:
      /*
       * We get a 'url' field back from the integration field when we create a Dynatrace status check from
       * any of its selector types. However, we do not want the URL value provided to populate the Problems URL
       * input field in these cases, as it is not a valid Problems URL (but rather an entities API URL).
       */
      isDynatraceSelectorEntityIdType(dynatraceSelector) ||
      isDynatraceSelectorK8sQueryType(dynatraceSelector)
        ? ''
        : monitorUrl,
    multipleRegions: Boolean(customDomainName),
    observabilityTool,
    observabilityToolNickname,
    statusCheckName,
    pollingIntervalSeconds,
    requestTimeout,
    ...(dynatraceSelector && { dynatraceSelector }),
    ...(grafanaSelector && { grafanaSelector }),
    ...(isAWSObservabilityTool &&
      externalIntegration && {
        awsAccessKeyID:
          externalIntegration.integrationSpecificValues?.AWS_ACCESS_KEY_ID ??
          '',
        awsSecretAccessKey:
          externalIntegration.integrationSpecificValues
            ?.AWS_SECRET_ACCESS_KEY ?? '',
        awsIamRoleArn:
          externalIntegration.integrationSpecificValues?.AWS_IAM_ROLE_ARN ?? ''
      }),
    ...(isPrometheusObservabilityTool && {
      prometheusSelector: {
        type: PrometheusSelectionSource.ALERT_URL
      }
    }),
    ...(multiSelectTags && { multiSelectTags })
  }
}

/**
 * Returns the URL to the monitor page for the given status check. Currently only Datadog is supported.
 */
export function getMonitorPageUrl(
  url: string,
  integrationType: IntegrationType
): string {
  if (integrationType === IntegrationType.Datadog) {
    const monitorId = getDatadogMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogEU) {
    const monitorId = getDatadogEUMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_eu}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS3) {
    const monitorId = getDatadogUS3MonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us3}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS5) {
    const monitorId = getDatadogUS5MonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us5}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS1Fed) {
    const monitorId = getDatadogUS1FEDMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us1_fed}/${monitorId}`
  }

  return url
}

/**
 * Returns the parameters required for deleting an external integration authentication for a status check
 */
export function getDeleteExternalIntegrationsStatusCheckAuthenticationParams(
  integration: TeamExternalIntegration | undefined,
  teamId?: string
) {
  if (!integration) return

  const { name, observabilityToolType, domain, type } = integration

  // Special case for Datadog US1 integration. The domain is not required for this integration type.
  const domainName = type === IntegrationType.Datadog || !domain ? null : domain

  return {
    name,
    domain: domainName,
    observabilityToolType,
    ...(teamId && { teamId })
  }
}

/**
 * Returns the parameters required for deleting an external integration authentication for a status check
 */
export function getDeleteExternalIntegrationsLoadGeneratorAuthenticationParams(
  integration: Pick<TeamExternalIntegration, 'name' | 'type'>,
  teamId?: string
) {
  const { name, type } = integration

  return {
    name,
    type: type.toUpperCase(),
    ...(teamId && { teamId })
  }
}

/**
 * Adds a space after the first colon in a status check tag
 */
export function formatStatusCheckTag(tag: string) {
  return tag.replace(/:/, ': ')
}

/**
 * Returns true if the passed in status check is a custom status check
 */
export function isCustomStatusCheck(statusCheck?: StatusCheck) {
  // Default to true if the status check is not defined
  if (!statusCheck) {
    return true
  }

  return isCustomUrl(
    statusCheck.endpointConfiguration?.url,
    statusCheck.teamExternalIntegration?.observabilityToolType
  )
}
